<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import type { TableOfContentsParagraph } from '../../utils/paragraph-schema/paragraphs'

const props =defineProps<{
  tableOfContents: TableOfContentsParagraph['tableOfContents']
}>()

const tocOpen = ref(false)
const viewportWidth = ref<number | null>(null)

const items = computed(() => props.tableOfContents?.items ?? [])
const title = computed(() => props.tableOfContents?.title ?? '')

const isMobileViewport = computed(() => (viewportWidth.value ?? 0) < 600)

const ariaLabel = computed(() => {
  if (!isMobileViewport.value) return
  return tocOpen.value
    ? 'Inhaltsverzeichnis schliessen'
    : 'Inhaltsverzeichnis öffnen'
})

const ariaControls = computed(() => isMobileViewport.value ? 'bx-aria-toc' : undefined)

const ariaExpanded = computed(() => tocOpen.value ? 'true' : 'false')

const toggleToc = () => {
  tocOpen.value = !tocOpen.value
}

const onResize = () => {
  viewportWidth.value = window.innerWidth
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  onResize()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})
</script>

<template>
  <template v-if="items.length">
    <div class="bx-content bx-table-of-contents bx-typo--table-of-contents"
         :class="{'bx-js-toc-hide': !tocOpen}"
         tabindex="0"
         role="button"
         :aria-label="ariaLabel"
         :aria-controls="ariaControls"
         :aria-expanded="ariaExpanded"
         @click="toggleToc"
         @keyup.enter="toggleToc">
      <div class="bx-table-of-contents__headline">
        <span>{{ title }}</span>
        <svg class="icon icon-arrow_toggle">
          <use href="#arrow_toggle" />
        </svg>
      </div>
      <ul id="bx-aria-toc"
          class="bx-table-of-contents__list">
        <li v-for="(item, index) in items"
            :key="item.href">
          <a class="bx-table-of-contents__links"
             :href="item.href"
             :data-index="index">
            {{ item.tocLabel || item.text }}
          </a>
        </li>
      </ul>
    </div>
  </template>
</template>
